import React from 'react';
import {connect} from 'react-redux';

import makeStyles from '@mui/styles/makeStyles';

import {fetchResource} from 'spectra-logic-ui/actions';
import {TableCellProps} from 'spectra-logic-ui/components/table';
import ExpandableTable from 'spectra-logic-ui/components/table/expandable';
import {Dispatch, Store} from 'spectra-logic-ui';

import {FilterType, FilterTypes} from '@/enum';
import {Filter, LifecycleV2, Destination} from '@/types';
import NoteCard from '@/components/note_card';

type Props = {
  lifecycle: LifecycleV2;
  destinations: Destination[];
  fetching: boolean;
  error: boolean;
  fetchDestinations: () => Promise<any>;
}

const useStyles = makeStyles({
  noteCards: {
    '& > *': {
      marginBottom: 15,
      '&:last-child': {
        marginBottom: 0,
      },
    },
  },
  nonNameCols: {
    width: '20%',
  },
});

const headers = ['Name', 'Type'];

const Rules = ({lifecycle, destinations, fetching, error, fetchDestinations}: Props) => {
  const rules = lifecycle.rules || [];
  const [selectedRuleIndex, setSelectedRuleIndex] = React.useState(-1);
  const classes = useStyles();

  React.useEffect(() => {
    if (fetchDestinations !== undefined) {
      fetchDestinations();
    }
  }, []);

  React.useEffect(() => {
    setSelectedRuleIndex(rules.length === 1 && rules[0].destinations ? 0 : -1);
  }, [rules]);

  return (
    <ExpandableTable>
      <ExpandableTable.Header>
        <ExpandableTable.HeaderRow>
          {headers.map((h, i) => {
            const extras = {} as TableCellProps;
            if (i > 0) {
              extras.className = classes.nonNameCols;
            }
            return <ExpandableTable.Cell key={h} {...extras}>{h}</ExpandableTable.Cell>;
          })}
        </ExpandableTable.HeaderRow>
      </ExpandableTable.Header>
      <ExpandableTable.Body isLoading={fetching} hasError={error} colSpan={headers.length}>
        {rules.map((rule, ruleIndex) => {
          const ruleType = rule.expiration ? 'Deletion' : 'Transfer';
          const deleteStorage = rule.deletion ?
            'Remove unselected storage' : 'Keep all existing storage';
          let destNames = null as any;
          let count = null as any;
          if (rule.destinations && rule.destinations.storage) {
            destNames = rule.destinations.storage.map((storage: string) => {
              const foundDest = destinations.find((dest) => dest.id == storage);
              return foundDest ? foundDest.name : null;
            });
            count = rule.destinations.count || 0;
            count = count === 0 ? 'All' : count;
          }
          return (
            <ExpandableTable.BodyRow
              key={ruleIndex} selected={ruleIndex === selectedRuleIndex}
              onClick={() => setSelectedRuleIndex(ruleIndex === selectedRuleIndex ? -1 : ruleIndex)}
              details={
                <>
                  <div className={classes.noteCards}>
                    {rule.destinations &&
                      <NoteCard title='Destinations'>
                        <NoteCard.Table>
                          <NoteCard.TableRow name='Count' value={count}/>
                          <NoteCard.TableRow name='Storage' value={destNames ? destNames.join(', ') : ''} />
                          <NoteCard.TableRow name='Delete' value={deleteStorage} />
                        </NoteCard.Table>
                      </NoteCard>
                    }
                    {rule.filters &&
                      <NoteCard title='Filters'>
                        <NoteCard.Table>
                          {rule.filters.map((filter, i) => (
                            <FilterRow key={i} filter={filter} />
                          ))}
                        </NoteCard.Table>
                      </NoteCard>
                    }
                  </div>
                </>
              }
            >
              <ExpandableTable.Cell>{rule.name}</ExpandableTable.Cell>
              <ExpandableTable.Cell>{ruleType}</ExpandableTable.Cell>
            </ExpandableTable.BodyRow>
          );
        })}
      </ExpandableTable.Body>
    </ExpandableTable>
  );
};

const FilterRow = ({filter}: {filter: Filter}) => {
  let filterTypeString = FilterTypes[filter.filter];
  const values = [];
  switch (filter.filter) {
  case FilterType.CURRENT:
    const currentPrefix = filter.exclude ? 'Non-Current' : 'Current';
    filterTypeString = `${currentPrefix} ${filterTypeString}`;
    if (filter.count !== null && filter.count !== undefined) {
      values.push(`${filter.count} versions`);
    }
    break;
  case FilterType.NAME:
  case FilterType.TAG:
    const prefix = filter.exclude ? 'Exclude' : 'Include';
    filterTypeString = `${prefix} ${filterTypeString}`;
    if (filter.value) {
      values.push(`Match: ${filter.value}`);
    }
    if (filter.key) {
      values.push(`Key: ${filter.key}`);
    }
    break;
  case FilterType.SCHEDULE:
    if (filter.count !== null && filter.count !== undefined) {
      let value = `${filter.count} day`;
      if (filter.count > 1) {
        value += 's';
      }
      values.push(value);
    }
    break;
  }
  return (
    <NoteCard.TableRow name={filterTypeString} value={values.join(', ')} />
  );
};

const mapStateToProps = (state: Store) => {
  const destinations = state.resources.destinations || {};
  return {
    destinations: destinations.data || [],
    error: destinations.error,
    fetching: destinations.fetching,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchDestinations: () => dispatch(fetchResource('destinations')),
});

export default connect(mapStateToProps, mapDispatchToProps)(Rules);
